import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../BaseUrl/ApiClient';
import { useHistory } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Input, Label, FormGroup } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faPencil, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { format, parseISO } from 'date-fns';

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function ProfileList() {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const history = useHistory();
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    search: false,
    date: false,
    status: false,
    type: false
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Subscription update modal state
  const [showUpdateSubscriptionModal, setShowUpdateSubscriptionModal] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState('');
  const [newSubscriptionType, setNewSubscriptionType] = useState('');
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);

  // Duplicate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [businessToDuplicate, setBusinessToDuplicate] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'MMM dd, yyyy');
    } catch (e) {
      return '-';
    }
  };

  const fetchBusinesses = async (page = 1, limit = pagination.limit, query = searchQuery, start = startDate, end = endDate, status = subscriptionStatus, type = subscriptionType) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      if (query) params.query = query;

      if (start) {
        const utcStart = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
        params.startDate = format(utcStart, 'yyyy-MM-dd');
      }

      if (end) {
        const utcEnd = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999));
        params.endDate = format(utcEnd, 'yyyy-MM-dd');
      }

      if (status) params.subscriptionStatus = status;
      if (type) params.subscriptionType = type;

      setActiveFilters({
        search: !!query,
        date: !!(start || end),
        status: !!status,
        type: !!type
      });

      const response = await ApiClient().get('/business/admin/list', { params });
      const { responseData } = response.data;
      setBusinesses(responseData.businesses);
      setPagination(responseData.pagination);
    } catch (error) {
      console.error('API Error:', error);
      if (error.response) {
        toast.error(error.response.data?.responseMessage?.[0] || "Failed to fetch businesses");
      } else if (error.request) {
        toast.error("Network error - no response received");
      } else {
        toast.error("Error setting up request");
      }
    } finally {
      setIsLoading(false);
      setDateLoading(false);
    }
  };

  const debouncedFetchBusinesses = useCallback(
    debounce((page, limit, query, start, end, status, type) => {
      fetchBusinesses(page, limit, query, start, end, status, type);
    }, 500),
    [pagination.limit]
  );

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchBusinesses(1, pagination.limit, query, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handlePageChange = (newPage) => {
    fetchBusinesses(newPage, pagination.limit, searchQuery, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination({ ...pagination, limit: newLimit });
    fetchBusinesses(1, newLimit, searchQuery, startDate, endDate, subscriptionStatus, subscriptionType);
  };

  const handleDeleteClick = (businessId, businessName) => {
    setBusinessToDelete({ id: businessId, name: businessName });
    setShowDeleteModal(true);
  };

  const handleViewDeals = (businessId) => {
    history.push(`/business-deals/${businessId}`);
  };


  const confirmDeleteBusiness = async () => {
    if (!businessToDelete) return;
    setIsDeleting(true);
    try {
      const response = await ApiClient().delete(`/business/admin/${businessToDelete.id}`);
      toast.success(response.data.responseMessage?.[0] || "Business deleted successfully");
      setShowDeleteModal(false);
      setBusinessToDelete(null);
      fetchBusinesses();
    } catch (error) {
      console.error("Delete error:", error);
      if (error.response) {
        toast.error(error.response.data?.responseMessage?.[0] || "Failed to delete business");
      } else {
        toast.error("Network error during deletion");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = (id) => history.push(`/update-business-profile/${id}`);
  const handleView = (id) => history.push(`/business-list-detail/${id}`);

  // Open subscription type modal
  const handleUpdateSubscriptionClick = (businessId, businessName) => {
    setSelectedBusinessId(businessId);
    setSelectedBusinessName(businessName);
    setNewSubscriptionType('');
    setShowUpdateSubscriptionModal(true);
  };

  // API call to update subscription type
  const updateSubscriptionType = async () => {
    if (!newSubscriptionType) {
      toast.error("Please select a subscription type");
      return;
    }

    setIsUpdatingSubscription(true);
    try {
      const response = await ApiClient().put(
        `/business/admin/subscriptionType/${selectedBusinessId}`,
        { subscriptionType: newSubscriptionType }
      );
      toast.success(response.data.responseMessage?.[0] || "Subscription type updated successfully");
      setShowUpdateSubscriptionModal(false);
      fetchBusinesses();
    } catch (error) {
      console.error("Update subscription error:", error);
      if (error.response) {
        toast.error(error.response.data?.responseMessage?.[0] || "Failed to update subscription type");
      } else {
        toast.error("Network error during update");
      }
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  // Open duplicate modal
  const handleDuplicateClick = (businessId, businessName) => {
    setBusinessToDuplicate({ id: businessId, name: businessName });
    setShowDuplicateModal(true);
  };

  // API call to duplicate business
  const confirmDuplicateBusiness = async () => {
    if (!businessToDuplicate) return;
    setIsDuplicating(true);
    try {
      const response = await ApiClient().post(`/business/admin/duplicate/${businessToDuplicate.id}`);
      toast.success(response.data.responseMessage?.[0] || "Business duplicated successfully");
      setShowDuplicateModal(false);
      setBusinessToDuplicate(null);
      fetchBusinesses();
    } catch (error) {
      console.error("Duplicate error:", error);
      if (error.response) {
        toast.error(error.response.data?.responseMessage?.[0] || "Failed to duplicate business");
      } else {
        toast.error("Network error during duplication");
      }
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />

      <div className="page-content">
        <MetaTags>
          <title>Business Profiles List</title>
        </MetaTags>
        <div className='d-flex justify-content-end' >
          <Button color="primary" className="mb-3 mr-3" onClick={() => history.push("/add-business-profile")}>
            Add New Business Profile
          </Button>
        </div>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h2 className="card-title mb-0">Business Profiles</h2>
                  <h2 className="card-title mb-0"> Total Business Profiles: {' '}
                    <span className="counter-value">{pagination.totalDocs}</span>
                  </h2>
                </CardHeader>
                <CardBody>
                  <div id="businessList">
                    <Row className="g-3 justify-content-end">
                      <Col md={4}>
                        <FormGroup>
                          <div className="search-box position-relative">
                            <Input
                              type="text"
                              id="search"
                              className="form-control ps-5"
                              placeholder="Search business profiles..."
                              value={searchQuery}
                              onChange={handleSearchInputChange}
                              onKeyPress={(e) => e.key === 'Enter' && fetchBusinesses(1)}
                            />
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="position-absolute"
                              style={{ top: '10px', left: '15px', color: '#6c757d' }}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              className={`clear-icon position-absolute ${!activeFilters.search ? 'd-none' : ''}`}
                              style={{ top: '12px', right: '10px', cursor: 'pointer' }}
                              onClick={() => { setSearchQuery(''); debouncedFetchBusinesses(1); }}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="businessTable">
                        <thead className="table-light">
                          <tr>
                            <th>Business Name</th>
                            <th>Location</th>
                            <th>Logo</th>
                            <th>Google Link</th>
                            <th>Actions</th>
                            <th>Deals</th>

                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {businesses.map((business, index) => (
                            <tr key={index}>
                              <td>{business.businessName || 'No Name'}</td>
                              <td>
                                {business.businessLocation
                                  ? (() => {
                                      const loc = `${business.businessLocation.streetAddress || ''} ${business.businessLocation.state || ''}, ${business.businessLocation.city || ''}, ${business.businessLocation.postalCode || ''}`.trim();
                                      const words = loc.split(/\s+/);
                                      const truncated = words.length > 6 ? words.slice(0, 6).join(' ') + '...' : loc;
                                      return <span title={loc}>{truncated}</span>;
                                    })()
                                  : 'No Location'}
                              </td>
                              <td>
                                {business.logoImage?.url ? (
                                  <img
                                    src={business.logoImage.url}
                                    alt="Business logo"
                                    className="img-thumbnail"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/path/to/default/image.png'; }}
                                  />
                                ) : <span className="text-muted">No logo</span>}
                              </td>
                              <td>
                                {business.googleBusinessLink ? (
                                  <a
                                    href={business.googleBusinessLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Link
                                  </a>
                                ) : (
                                  <span className="text-muted">No Link</span>
                                )}
                              </td>

                              <td className='d-flex pt-4 pb-3 gap-2'>
                                <Button color="success" size="sm" onClick={() => handleView(business.id)}>
                                  View
                                </Button>

                                <Button color="primary" size="sm" onClick={() => handleUpdate(business.id)}>
                                  <FontAwesomeIcon icon={faPencil} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteClick(business.id, business.businessName)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                                <Button className="text-white bg-primary" size="sm" onClick={() => handleDuplicateClick(business.id, business.businessName)}>
                                  Duplicate Profile
                                </Button>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  onClick={() => handleViewDeals(business.id)}
                                >
                                  View Deals
                                </Button>


                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {isLoading && businesses.length === 0 && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="mt-2">Loading Business Profiles...</h5>
                        </div>
                      )}

                      {businesses.length === 0 && !isLoading && (
                        <div className="text-center py-4">
                          <h5 className="mt-2">No Business Profiles Found</h5>
                          <p className="text-muted">
                            {activeFilters.search || activeFilters.date || activeFilters.status || activeFilters.type
                              ? "Try adjusting your filters"
                              : "No business profiles available"}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-3">
                      <div className="pagination-wrap hstack gap-2">
                        <Button color="light" disabled={pagination.page === 1} onClick={() => handlePageChange(pagination.page - 1)}>Previous</Button>
                        <div className="d-flex gap-1">
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) pageNum = i + 1;
                            else if (pagination.page <= 3) pageNum = i + 1;
                            else if (pagination.page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                            else pageNum = pagination.page - 2 + i;
                            return (
                              <Button key={pageNum} color={pagination.page === pageNum ? 'primary' : 'light'} onClick={() => handlePageChange(pageNum)}>{pageNum}</Button>
                            );
                          })}
                        </div>
                        <Button color="light" disabled={pagination.page === pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>Next</Button>
                        <select className="form-select" value={pagination.limit} onChange={handleLimitChange} style={{ width: '80px' }}>
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete <strong>{businessToDelete?.name}</strong>?
            </div>
            <div className="modal-footer">
              <Button color="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button color="danger" onClick={confirmDeleteBusiness} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Update Subscription Type Modal */}
      <div className={`modal fade ${showUpdateSubscriptionModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Subscription Type for {selectedBusinessName}</h5>
              <button type="button" className="btn-close" onClick={() => setShowUpdateSubscriptionModal(false)}></button>
            </div>
            <div className="modal-body">
              <FormGroup>
                <Label>Select Subscription Type</Label>
                <div>
                  <FormGroup check inline>
                    <Input
                      type="radio"
                      name="subscriptionType"
                      value="sponsored"
                      checked={newSubscriptionType === 'sponsored'}
                      onChange={(e) => setNewSubscriptionType(e.target.value)}
                    />
                    <Label check>Sponsored</Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Input
                      type="radio"
                      name="subscriptionType"
                      value="standard"
                      checked={newSubscriptionType === 'standard'}
                      onChange={(e) => setNewSubscriptionType(e.target.value)}
                    />
                    <Label check>Standard</Label>
                  </FormGroup>
                </div>
              </FormGroup>
            </div>
            <div className="modal-footer">
              <Button color="secondary" onClick={() => setShowUpdateSubscriptionModal(false)}>Cancel</Button>
              <Button color="primary" onClick={updateSubscriptionType} disabled={isUpdatingSubscription}>
                {isUpdatingSubscription ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Duplicate Confirmation Modal */}
      <div className={`modal fade ${showDuplicateModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Duplicate</h5>
              <button type="button" className="btn-close" onClick={() => setShowDuplicateModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to duplicate <strong>{businessToDuplicate?.name}</strong>?
            </div>
            <div className="modal-footer">
              <Button color="secondary" onClick={() => setShowDuplicateModal(false)}>Cancel</Button>
              <Button color="primary" onClick={confirmDuplicateBusiness} disabled={isDuplicating}>
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </Button>
            </div>
          </div>
        </div>
      </div>

    </React.Fragment>
  );
}

export default ProfileList;
